/**
 * The rows the Supplier Performance table writes to Excel — CR-7288.
 *
 * The export used to write ONE row per supplier, and nothing below it: no
 * customer site, no supplier code, no column for either. That made it unable to
 * answer the question the page's tiers exist for — which account did this work
 * belong to — and reconciliation is precisely when somebody reaches for Excel.
 * Aaron, 18 Sep: *"yes absolutely fix the export"*.
 *
 * The sheet is FLAT and pivot-friendly rather than an indented copy of the
 * screen: every row carries `Level`, `Customer Site` and `Supplier Code`, so a
 * filter on Level gives back exactly the old export and a pivot on Supplier
 * Code gives the breakdown. One column set for all three levels, built by one
 * function — the screen's three tiers already taught us what a second
 * implementation of the same row costs.
 *
 * COST. The obvious build — walk the screen's fan-out for every supplier — is
 * one request per supplier per site per code, thousands on a real tenant. This
 * inverts it: the list endpoint returns EVERY supplier for a given scope, so
 * one request per SITE and one per (site, code) covers the whole tenant.
 * Gardner, measured on the staging copy: 8 customer sites and 4 relationships
 * holding two codes each, so 1 + 8 + 8 = 17 requests for an export of EVERY
 * supplier — against 16 for ONE expanded supplier on screen (7 sites + 9
 * codes, investigation.md). Round 2 adds one request per split site that also
 * carries uncoded work — so 23 on that copy (1 + 8 sites + 11 code pairs + 3 buckets),
 * MEASURED rather than derived: 17 and then 20 were both published from arithmetic that was
 * never run —
 * because the `(no code)` row was on screen and missing from the file. The
 * point is unchanged: the figure does not grow with the supplier count.
 *
 * WEIGHT (28 Sep 2026). Bounded in COUNT is not bounded in cost. Every PNA
 * supplier holds its own code AND uncoded work, so its one site splits: 216
 * (site, code) requests, each computing EVERY supplier (796 rows) to keep one,
 * all fired at once — 113 s locally, and a certain router timeout on a live
 * dyno. A code held by ONE supplier is now asked for that supplier only (the
 * screen's own code-row request), only the pairs a row below actually reads are
 * asked for, and at most `MAX_IN_FLIGHT` requests run at a time. Never more
 * requests than before; the same rows.
 */
import { suppliersAPI, SupplierProfile, SupplierCustomerSite } from '../../lib/api';
import { siteSplitsByCode, hasNoScopedActivity } from './utils';

export type ExportLevel = 'Supplier' | 'Customer Site' | 'Supplier Code';

export interface ExportScopeRow {
  supplier: SupplierProfile;
  level: ExportLevel;
  siteName: string;
  code: string;
}

type Params = Record<string, string | number | boolean>;

/** Requests the export keeps in flight at once — enough to overlap, few enough
 * that one export cannot queue a web dyno past its router timeout. */
export const MAX_IN_FLIGHT = 6;

/** ``task`` over ``items`` with at most ``limit`` running at once, results in order. */
async function inFlightLimited<T, R>(items: T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next;
      next += 1;
      results[i] = await task(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/** Every distinct site across the rows, and the codes each one holds. */
export function sitesInScope(rows: SupplierProfile[]): Map<number, SupplierCustomerSite> {
  const out = new Map<number, SupplierCustomerSite>();
  for (const row of rows) {
    for (const site of row.customer_sites ?? []) {
      const seen = out.get(site.id);
      if (!seen) {
        out.set(site.id, { ...site, supplier_codes: [...(site.supplier_codes ?? [])] });
        continue;
      }
      // The same site can appear under several suppliers with different codes;
      // the export needs the union, or a supplier's code row goes missing.
      //
      // Round 3 (blind stage 2): `has_uncoded_work` is per (SUPPLIER, site) and
      // this merge kept whichever supplier was seen FIRST. Where the first had
      // none and a later one did, the site was left out of the bucket fan-out
      // entirely and that later supplier's `(no code)` row vanished from the
      // file — its code rows then did not sum to its site row, which is the one
      // invariant this tier is judged on. It is an OR, for the same reason the
      // codes are a union.
      seen.has_uncoded_work = seen.has_uncoded_work || site.has_uncoded_work === true;
      for (const code of site.supplier_codes ?? []) {
        if (!seen.supplier_codes.includes(code)) seen.supplier_codes.push(code);
      }
    }
  }
  return out;
}

/**
 * Fetch the site and code rows behind an export, in one pass over the scope.
 *
 * @param supplierRows - the supplier-level rows already fetched
 * @param buildParams - the page's export params (all pages, current filters)
 * @returns supplier rows followed by their site rows and code rows, in screen order
 */
export async function buildExportScopeRows(
  supplierRows: SupplierProfile[],
  buildParams: (extra: Params) => Params,
): Promise<ExportScopeRow[]> {
  const sites = sitesInScope(supplierRows);
  if (sites.size === 0) {
    return supplierRows.map((supplier) => ({ supplier, level: 'Supplier', siteName: '', code: '' }));
  }

  // One request per site, and one per (site, code) where the site holds more
  // than one — the same rule the screen's expander uses, so the file and the
  // page agree about which sites are worth splitting.
  // Array.from rather than spread / for-of over the Map: this project's
  // tsconfig target predates es2015 iterables, so iterating a Map directly is
  // a compile error (TS2802) even though it reads fine.
  const siteIds = Array.from(sites.keys());
  // The (site, code) pairs — and (no code) sites — the rows below READ, with
  // the suppliers each is read for. The rows are written per (supplier, site),
  // from `siteSplitsByCode` on the supplier's OWN entry, so that is what
  // decides; the site-wide union above only names the sites.
  //
  // Round 2: the `(no code)` bucket is a ROW on screen and was missing from
  // the file entirely, so the export's code rows did not sum to the site row
  // above them wherever a site carried uncoded work — the one invariant this
  // tier is built on, broken in the artefact the customer actually keeps.
  const codeHolders = new Map<string, { siteId: number; code: string; suppliers: Set<number> }>();
  const noCodeHolders = new Map<number, Set<number>>();
  supplierRows.forEach((supplier) => (supplier.customer_sites ?? []).forEach((site) => {
    if (!siteSplitsByCode(site)) return;
    (site.supplier_codes ?? []).forEach((code) => {
      const key = `${site.id}|${code}`;
      const pair = codeHolders.get(key) ?? { siteId: site.id, code, suppliers: new Set<number>() };
      pair.suppliers.add(supplier.company);
      codeHolders.set(key, pair);
    });
    if (site.has_uncoded_work === true) {
      const holders = noCodeHolders.get(site.id) ?? new Set<number>();
      holders.add(supplier.company);
      noCodeHolders.set(site.id, holders);
    }
  }));

  // A scope read for ONE supplier is asked for that supplier alone — the
  // screen's own code-row request, one row computed instead of every supplier.
  const onlyHolder = (holders: Set<number>): Params => (
    holders.size === 1 ? { company_id: Array.from(holders)[0] } : {});
  const codePairs = Array.from(codeHolders.values());
  const noCodeSiteIds = Array.from(noCodeHolders.keys());
  const requests: Params[] = [
    ...siteIds.map((siteId) => ({ site: siteId })),
    ...codePairs.map(({ siteId, code, suppliers }) => ({ site: siteId, supplier_code: code, ...onlyHolder(suppliers) })),
    ...noCodeSiteIds.map((siteId) => ({
      site: siteId, supplier_code_none: 'true', ...onlyHolder(noCodeHolders.get(siteId) ?? new Set<number>()),
    })),
  ];
  const responses = await inFlightLimited(
    requests, MAX_IN_FLIGHT, (extra) => suppliersAPI.getProfiles(buildParams(extra)));

  const bySite = new Map<number, SupplierProfile[]>();
  siteIds.forEach((siteId, i) => bySite.set(siteId, responses[i].data.results));
  const byCode = new Map<string, SupplierProfile[]>();
  codePairs.forEach(({ siteId, code }, i) => byCode.set(
    `${siteId}|${code}`, responses[siteIds.length + i].data.results));
  const byNoCode = new Map<number, SupplierProfile[]>();
  noCodeSiteIds.forEach((siteId, i) => byNoCode.set(
    siteId, responses[siteIds.length + codePairs.length + i].data.results));

  const out: ExportScopeRow[] = [];
  for (const supplier of supplierRows) {
    out.push({ supplier, level: 'Supplier', siteName: '', code: '' });
    for (const site of supplier.customer_sites ?? []) {
      const siteRow = (bySite.get(site.id) ?? []).find((r) => r.company === supplier.company);
      if (!siteRow) continue;
      out.push({ supplier: siteRow, level: 'Customer Site', siteName: site.name, code: '' });
      if (!siteSplitsByCode(site)) continue;
      for (const code of site.supplier_codes) {
        const codeRow = (byCode.get(`${site.id}|${code}`) ?? []).find((r) => r.company === supplier.company);
        // An issued code with no work keeps its row here as it does on screen:
        // a zero against a named account is an answer, not a gap.
        if (codeRow) out.push({ supplier: codeRow, level: 'Supplier Code', siteName: site.name, code });
      }
      if (site.has_uncoded_work === true) {
        const noCodeRow = (byNoCode.get(site.id) ?? []).find((r) => r.company === supplier.company);
        // Same words as the screen uses, so a reader matching the file against
        // the page is not left translating between two vocabularies.
        //
        // Round 3 (blind stage 2): and the SAME emptiness test as the screen.
        // `has_uncoded_work` is computed over all time, while every figure on
        // the row is windowed — so a site whose uncoded work is all outside the
        // period got a bucket row of zeros in the FILE while the page hid it.
        // The file and the page have to agree about which rows exist.
        if (noCodeRow && !hasNoScopedActivity(noCodeRow as unknown as Record<string, unknown>)) {
          out.push({
            supplier: noCodeRow, level: 'Supplier Code', siteName: site.name, code: 'No code recorded',
          });
        }
      }
    }
  }
  return out;
}
